// src/hooks/useReliableGeolocation.ts  ← create this file first
import { useState, useCallback, useEffect } from 'react';

export const useReliableGeolocation = ()=> {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [permissionState, setPermissionState] = useState('unknown');

  const fetchLocation = useCallback(async (highAccuracy = false) => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by this browser.");
      return null;
    }

    setLoading(true);
    setError(null);

    return new Promise((resolve) => {
      const options = {
        enableHighAccuracy: highAccuracy,
        timeout: highAccuracy ? 60000 : 45000,     // 60s for high acc, 45s normal
        maximumAge: 120000,                        // accept up to 2 min old
      };

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLoading(false);
          const result = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          };
          setLocation(result);
          resolve(result);
        },
        (err) => {
          setLoading(false);

          let message = "Failed to get location.";
          switch (err.code) {
            case 1: // PERMISSION_DENIED
              message = "Location permission was denied. Please enable it in your browser and phone settings.";
              break;
            case 2: // POSITION_UNAVAILABLE
              message = "Location information is unavailable right now. Try moving to an open area.";
              break;
            case 3: // TIMEOUT
              message = "Location request timed out. This can happen on slow networks or indoors.";
              break;
            default:
              message = err.message || "Unknown location error.";
          }

          setError(message);
          resolve(null);
        },
        options
      );
    });
  }, []);

  const getLocation = useCallback(async () => {
    // First try with normal accuracy (more reliable)
    let pos = await fetchLocation(false);
    if (!pos) {
      // If failed, retry with high accuracy (sometimes helps Android)
      pos = await fetchLocation(true);
    }
    return pos;
  }, [fetchLocation]);

  // Check permission status
  useEffect(() => {
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: 'geolocation'  })
        .then((status) => {
          setPermissionState(status.state);
          status.onchange = () => setPermissionState(status.state);
        })
        .catch(() => setPermissionState('unknown'));
    } else {
      setPermissionState('unknown');
    }
  }, []);

  return {
    location,
    error,
    loading,
    permissionState,
    getLocation,
    retry: getLocation,
  };
};